import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { InvitePreview } from '@ourcove/types';
import { api } from '../lib/api';
import { useStore } from '../lib/store';

function pwStrength(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (pw.length < 6) return 0;
  let s: 0 | 1 | 2 | 3 | 4 = 1;
  if (pw.length >= 10) s = 2;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw) && /\d/.test(pw)) s = 3;
  if (/[^A-Za-z0-9]/.test(pw) && pw.length >= 10) s = 4;
  return s;
}

function pwHint(s: number): string {
  return ['At least 6 characters.', 'Getting there — try a longer one.',
          'Good — add a number or symbol to strengthen.',
          'Solid. A symbol would make it stronger still.',
          'Strong password. Nice.'][s];
}

export function Invite() {
  const { token = '' } = useParams();
  const nav = useNavigate();
  const setAuth = useStore((s) => s.setAuth);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.invitePreview(token).then(setPreview).catch((e) => setErr(e.message));
  }, [token]);

  const strength = useMemo(() => pwStrength(pw), [pw]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await api.acceptInvite(token, name.trim(), pw);
      setAuth(r.accessToken, r.user, r.cove);
      nav('/');
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (err && !preview) {
    return (
      <div className="app">
        <div className="auth-hero">
          <h1 className="display auth-title">Something's off.</h1>
          <p className="muted auth-sub text-center">{err}</p>
          <p className="auth-footnote">Ask whoever sent you the link to send a fresh one — invites expire.</p>
        </div>
      </div>
    );
  }
  if (!preview) return <div className="app" />;

  const initial = preview.inviterDisplayName?.trim().charAt(0).toUpperCase() || '?';

  return (
    <div className="app">
      <div className="auth-hero">
        <p className="eyebrow">You've been invited</p>
        <h1 className="display auth-title">
          Join <br />{preview.coveName}.
        </h1>

        <div className="inviter-card">
          <div className="row-top">
            <div className="avatar md" data-tone="clay">{initial}</div>
            <div className="who">
              <div className="label">Invited by</div>
              <div className="name">{preview.inviterDisplayName}</div>
            </div>
          </div>
          {preview.note && <div className="inviter-note">"{preview.note}"</div>}
        </div>

        <form className="auth-form" onSubmit={submit}>
          <label className="field">
            <span className="field-label">Your name</span>
            <input
              className="input"
              placeholder="e.g. Ana Alvarez"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoCapitalize="words"
              autoComplete="name"
            />
          </label>
          <label className="field">
            <span className="field-label">Choose a password</span>
            <input
              className="input"
              type="password"
              placeholder="at least 6 characters"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
            />
            <div className="pw-strength">
              {[1, 2, 3, 4].map((i) => (
                <span key={i} className={strength >= i ? 'on' : ''} />
              ))}
            </div>
            <div className="pw-strength-hint">{pwHint(strength)}</div>
          </label>
          {err && <div className="error">{err}</div>}
          <button className="btn block" type="submit" disabled={busy || !name || pw.length < 6}>
            {busy ? 'Joining…' : 'Accept & join the cove'}
          </button>
          <div className="trust-line">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="var(--sage-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 8 L6 12 L14 3" /></svg>
            Invite expires soon · only usable once
          </div>
        </form>
      </div>
    </div>
  );
}
